/**
 * Leads Service
 * Handles all lead-related API operations
 */

import apiClient from "./api";

class LeadsService {
  /**
   * Get list of leads with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.event - Filter by event code
   * @param {string} params.lead_type - Filter by lead type
   * @param {string} params.works_with_us - Filter by works_with_us status
   * @param {string} params.commission_model - Filter by commission model
   * @param {string} params.contact - Filter by contact (phone/email/telegram)
   * @param {string} params.search - Search query
   * @param {number} params.limit - Number of results per page (default: 20)
   * @param {number} params.offset - Offset for pagination (default: 0)
   * @param {string} params.sort_by - Sort field (default: 'created_at')
   * @param {string} params.sort_order - Sort order: 'asc' or 'desc' (default: 'desc')
   * @returns {Promise<Object>} Response with leads array and pagination info
   */
  async getLeads(params = {}) {
    const response = await apiClient.get("/leads", params);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to fetch leads");
    }
    
    return response.data;
  }

  /**
   * Get single lead by ID
   * @param {string} leadId - Lead UUID
   * @returns {Promise<Object>} Lead data
   */
  async getLead(leadId) {
    const response = await apiClient.get(`/leads/${leadId}`);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to fetch lead");
    }
    
    return response.data;
  }

  /**
   * Create new lead
   * @param {Object} leadData - Lead data
   * @returns {Promise<Object>} Created lead
   */
  async createLead(leadData) {
    const response = await apiClient.post("/leads", leadData);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to create lead");
    }
    
    return response.data;
  }

  /**
   * Update existing lead
   * @param {string} leadId - Lead UUID
   * @param {Object} leadData - Updated lead data (partial)
   * @returns {Promise<Object>} Updated lead
   */
  async updateLead(leadId, leadData) {
    const response = await apiClient.put(`/leads/${leadId}`, leadData);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to update lead");
    }
    
    return response.data;
  }

  /**
   * Delete lead (soft delete)
   * @param {string} leadId - Lead UUID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteLead(leadId) {
    const response = await apiClient.delete(`/leads/${leadId}`);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to delete lead");
    }
    
    return response.data;
  }

  /**
   * Check for duplicate leads
   * @param {string} contact - Contact to check (phone/email/telegram)
   * @returns {Promise<Object>} Duplicate check result
   */
  async checkDuplicate(contact) {
    const response = await apiClient.post("/leads/check-duplicate", { contact });
    
    if (!response.success) {
      throw new Error(response.message || "Failed to check duplicate");
    }
    
    return response.data;
  }

  /**
   * Export leads to CSV
   * @param {Object} params - Filter parameters
   * @returns {Promise<Object>} Export result with download URL
   */
  async exportCSV(params = {}) {
    // Добавляем часовой пояс пользователя
    const timezoneOffset = new Date().getTimezoneOffset(); // в минутах (отрицательное значение для UTC+, положительное для UTC-)
    
    const queryString = new URLSearchParams(
      Object.fromEntries(
        Object.entries({
          ...params,
          timezone_offset: -timezoneOffset // Инвертируем знак для правильного смещения
        }).filter(([_, v]) => v != null)
      )
    ).toString();

    const url = queryString 
      ? `/leads/export/csv?${queryString}` 
      : "/leads/export/csv";

    const response = await apiClient.get(url);

    if (!response.success) {
      throw new Error(response.message || "Failed to export CSV");
    }

    return response.data;
  }

  /**
   * Get all leads (admin only) with filters and pagination
   * @param {Object} params - Query parameters
   * @param {string} params.event - Filter by event code
   * @param {string} params.lead_type - Filter by lead type
   * @param {string} params.traffic_source - Filter by traffic source
   * @param {string} params.works_with_us - Filter by works_with_us status
   * @param {string} params.commission_model - Filter by commission model
   * @param {string} params.manager_telegram_ids - Comma-separated manager IDs
   * @param {string} params.search - Search query
   * @param {number} params.limit - Number of results per page (default: 50)
   * @param {number} params.offset - Offset for pagination (default: 0)
   * @returns {Promise<Object>} Response with leads array and pagination info
   */
  async getAllLeads(params = {}) {
    const response = await apiClient.get("/leads/all", params);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to fetch all leads");
    }
    
    return response.data;
  }

  /**
   * Share lead via Telegram (sends deep link to user's chat)
   * @param {string} leadId - Lead UUID
   * @returns {Promise<Object>} Share result
   */
  async shareLead(leadId) {
    const response = await apiClient.post(`/leads/${leadId}/share`);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to share lead");
    }
    
    return response.data;
  }

  /**
   * Get share link for lead (without sending message)
   * @param {string} leadId - Lead UUID
   * @returns {Promise<Object>} Share link data
   */
  async getShareLink(leadId) {
    const response = await apiClient.get(`/leads/${leadId}/share-link`);
    
    if (!response.success) {
      throw new Error(response.message || "Failed to get share link");
    }
    
    return response.data;
  }
}

export const leadsService = new LeadsService();
export default leadsService;
